import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarInteiro, formatarReais } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { api, type FuncaoComValorHora } from './api';
import {
  corpoDoValorHora,
  efeitoDoValorHora,
  faltaNoValorHora,
  funcoesSemValor,
  inicioSugerido,
  valorHoraEscrito,
  vigenciaEscrita,
  type ValoresDoValorHora,
} from './valor-hora';

/**
 * O VALOR-HORA POR FUNÇÃO (0095) na tela de custos, ao lado do critério do
 * combustível — os dois são parâmetros da empresa que mudam o custo de todo o
 * histórico consultado, e por isso pedem motivo.
 *
 * A lista mostra TODAS as funções, inclusive as sem valor: a função sem valor
 * é a lacuna que deixa hora de pessoa fora do custo, e escondê-la seria
 * esconder por que o total é parcial. O valor é da função, nunca da pessoa.
 *
 * Sem desenho próprio no manual: segue o formulário do critério (cartão com
 * título e ação secundária) e a janela de até seis campos (p.30).
 */
export function ValorHoraDasFuncoes({ podeGravar }: { readonly podeGravar: boolean }) {
  const base = useId();
  const cliente = useQueryClient();
  const consulta = useQuery({ queryKey: ['custos', 'valor-hora'], queryFn: api.valorHora });
  const funcoes = useMemo(() => consulta.data?.funcoes ?? [], [consulta.data]);
  const ativas = useMemo(() => funcoes.filter((f) => f.ativo), [funcoes]);

  const vazio: ValoresDoValorHora = { funcaoId: '', desde: inicioSugerido(new Date()), valor: '', motivo: '' };
  const [aberta, setAberta] = useState(false);
  const [valores, setValores] = useState<ValoresDoValorHora>(vazio);
  const [tentou, setTentou] = useState(false);
  const falta = faltaNoValorHora(valores);
  const funcaoEscolhida = funcoes.find((f) => f.id === valores.funcaoId);

  const abrir = (funcaoId = '') => {
    setValores({ ...vazio, funcaoId });
    setTentou(false);
    definir.reset();
    setAberta(true);
  };

  const definir = useMutation({
    mutationFn: (v: ValoresDoValorHora) => api.definirValorHora(corpoDoValorHora(v)),
    meta: {
      retorno: retornoDe<{ valorHora: number; desde: string }, ValoresDoValorHora>((r, v) => {
        const f = funcoes.find((x) => x.id === v.funcaoId);
        return {
          titulo: 'Valor-hora da função definido',
          ...(f ? { alvo: { codigo: f.codigo, nome: f.nome } } : {}),
          fatos: [`${formatarReais(r.valorHora)}/h`, `motivo: ${v.motivo.trim()}`],
          efeito: efeitoDoValorHora(f, r.desde, r.valorHora),
        };
      }),
      /* A recusa aparece no topo da janela (p.23), e não de novo num toast. */
      silenciarFalha: true,
    },
    onSuccess: () => {
      setAberta(false);
      void cliente.invalidateQueries({ queryKey: ['custos'] });
    },
  });

  const semValor = funcoesSemValor(funcoes);

  const COLUNAS: readonly Coluna<FuncaoComValorHora>[] = [
    {
      chave: 'funcao',
      rotulo: 'Função',
      celula: (f) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{f.codigo}</span>
          <TextoDaCelula texto={f.ativo ? f.nome : `${f.nome} · desativada`} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'pessoas',
      rotulo: 'Pessoas hoje',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (f) => <span className="numero text-tinta">{formatarInteiro(f.pessoas)}</span>,
    },
    {
      chave: 'valor',
      rotulo: 'Valor-hora',
      alinhamento: 'numero',
      celula: (f) =>
        f.valorHoraVigente === null ? (
          /* A lacuna em selo preenchido (âmbar, atenção): a palavra vem junto da cor. */
          <span className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 whitespace-nowrap rounded-pill bg-atencao-fundo px-2.5 text-atencao-texto">
            <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-atencao" />
            <span className="txt-selo">Sem valor hoje</span>
          </span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">{valorHoraEscrito(f.valorHoraVigente)}</span>
        ),
    },
    {
      chave: 'vigencia',
      rotulo: 'Vigência',
      prioridade: 'secundaria',
      celula: (f) => {
        const atual = f.historico.find((h) => h.desde === f.vigenteDesde);
        const anteriores = f.historico.length - (atual ? 1 : 0);
        return (
          <span className="flex min-w-0 flex-col">
            <span className="txt-corpo text-tinta">{atual ? vigenciaEscrita(atual) : f.historico.length > 0 ? 'Só valores de outros períodos' : 'Nunca teve valor'}</span>
            {anteriores > 0 ? (
              <span className="txt-apoio text-tinta-apagada">
                {anteriores === 1 ? '1 valor em outro período' : `${formatarInteiro(anteriores)} valores em outros períodos`}
              </span>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-labelledby={`${base}-titulo`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-[80ch] flex-col gap-1">
          <h2 id={`${base}-titulo`} className="txt-secao text-tinta">
            Valor-hora por função
          </h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O custo da hora carregado — salário e encargos — de cada função, e não de cada pessoa. A hora da O.S. e a do turno
            entram no custo pelo valor da função que a pessoa exercia no dia. Hora sem função ou sem valor fica fora do total, e
            o total diz quantas.
            {semValor > 0 ? ` ${semValor === 1 ? '1 função ativa está' : `${formatarInteiro(semValor)} funções ativas estão`} sem valor hoje.` : ''}
          </p>
        </div>
        {/* Sem `valor_hora.gravar` o botão não aparece (prova do marco,
            07/10/2026): antes ele aparecia desligado, com a licença na dica. */}
        {podeGravar ? (
          <Botao
            nivel="secundario"
            disabled={ativas.length === 0}
            title={ativas.length === 0 ? 'Cadastre as funções em Pessoas primeiro.' : undefined}
            onClick={() => {
              abrir();
            }}
          >
            Definir valor-hora
          </Botao>
        ) : null}
      </div>

      <Tabela
        rotulo="Valor-hora por função"
        colunas={COLUNAS}
        linhas={funcoes}
        chaveDaLinha={(f) => f.id}
        carregando={consulta.isPending}
        alturaDupla
        {...(podeGravar
          ? {
              acoes: (f: (typeof funcoes)[number]) => (
                <MaisAcoes
                  acoes={[
                    {
                      rotulo: 'Definir novo valor',
                      aoEscolher: () => {
                        abrir(f.id);
                      },
                      ...(!f.ativo ? { motivoIndisponivel: 'A função está desativada. Reative-a em Pessoas.' } : {}),
                    },
                  ]}
                />
              ),
            }
          : {})}
        erro={consulta.isError ? <ErroDaLista oQue="As funções" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={
          <EstadoVazio
            titulo="Nenhuma função cadastrada"
            descricao="O valor-hora é por função. Cadastre as funções — operador, mecânico, ajudante — e quem exerce cada uma em Pessoas, e volte aqui para dar o valor."
            acao={
              <Link to="/pessoas" className="botao botao-secundario">
                Abrir pessoas
              </Link>
            }
          />
        }
      />

      <Janela
        titulo="Definir valor-hora"
        descricao="Vale a partir da data, até a próxima vigência. Na mesma data de início, corrige o valor."
        aberta={aberta && podeGravar}
        aoFechar={() => {
          setAberta(false);
        }}
        acoes={
          <>
            <Botao
              nivel="terciario"
              onClick={() => {
                setAberta(false);
              }}
            >
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={definir.isPending}
              onClick={() => {
                setTentou(true);
                if (Object.keys(falta).length > 0) return;
                definir.mutate(valores);
              }}
            >
              {definir.isPending ? 'Gravando…' : 'Gravar valor-hora'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {definir.isError ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
              <span className="txt-corpo text-tinta">{explicarFalha(definir.error).erro}</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(definir.error).comoResolver}</span>
            </div>
          ) : null}
          <CampoSelecao
            rotulo="Função"
            valor={valores.funcaoId}
            aoMudar={(funcaoId) => {
              setValores({ ...valores, funcaoId });
            }}
            opcoes={ativas.map((f) => ({ valor: f.id, rotulo: `${f.codigo} · ${f.nome}` }))}
            rotuloVazio="Escolha a função"
          />
          {tentou && falta.funcaoId ? <p className="txt-ajuda text-erro">{falta.funcaoId}</p> : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo
              id={`${base}-desde`}
              rotulo="Vale a partir de"
              erro={tentou ? (falta.desde ?? null) : null}
              ajuda="Sugerido: o 1º dia do mês anterior, para o mês fechado já sair com mão de obra."
            >
              <input
                id={`${base}-desde`}
                type="date"
                className="controle"
                value={valores.desde}
                onChange={(e) => {
                  setValores({ ...valores, desde: e.target.value });
                }}
              />
            </Campo>
            <CampoTexto
              rotulo="Valor da hora (R$)"
              valor={valores.valor}
              aoMudar={(valor) => {
                setValores({ ...valores, valor });
              }}
              placeholder="48,50"
              ajuda={
                funcaoEscolhida?.valorHoraVigente != null
                  ? `Hoje: ${valorHoraEscrito(funcaoEscolhida.valorHoraVigente) ?? ''}.`
                  : 'Salário e encargos por hora trabalhada.'
              }
              validar={(texto) => faltaNoValorHora({ ...valores, valor: texto }).valor ?? null}
            />
          </div>
          <CampoDoMotivo
            rotulo="Motivo"
            valor={valores.motivo}
            aoMudar={(motivo) => {
              setValores({ ...valores, motivo });
            }}
            exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['valor_hora.definir']}`}
          />
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O valor muda o custo de mão de obra de todo período consultado que cair na vigência, inclusive meses já vistos.
          </p>
        </div>
      </Janela>
    </section>
  );
}
